/**
 * AskAdmin — the viewer's way forward on an owner/admin-only action.
 * Names the workspace owners/admins (GET settings/team/members, any member)
 * with a mailto link each; the mail body carries this page's URL. Admins
 * without an email are skipped (never `mailto:null`).
 * Loading → skeleton. Error, or no admin email → "Copy link to this page"
 * (to paste to whoever administers the workspace) + Retry.
 * `compact` renders a single inline link for table rows ("Ask Nadia to attest").
 * `exclude` drops a user (the viewer: never a mailto to yourself); `primary`
 * renders the links as the filled primary (CMMC Audit, ready to sign).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@aigovops/design-system';
import { listWorkspaceMembers } from '../lib/api';
import './ask-admin.css';

export function AskAdmin({ slug, what, subject, compact, verb, lead = 'An owner or admin', roles = ['owner', 'admin'], prefer, single, exclude, primary }: {
  slug: string; what: string; subject: string; compact?: boolean; verb?: string;
  /** Who does it, as the sentence's subject ("An editor"). */
  lead?: string;
  /** Roles worth asking (default owner + admin). */
  roles?: string[];
  /** A user to ask first when they hold one of `roles` (e.g. the finding's owner). */
  prefer?: string | null;
  /** One link (the preferred person, else the first); the rest behind a quiet "or someone else". */
  single?: boolean;
  /** A user never to ask (the viewer). */
  exclude?: string | null;
  /** Render the ask links as the filled primary. */
  primary?: boolean;
}) {
  const q = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), enabled: !!slug, retry: false, staleTime: 60_000 });
  const [copied, setCopied] = useState('');
  const [more, setMore] = useState(false);
  const admins = (q.data?.members ?? [])
    .filter(m => roles.includes(m.role) && typeof m.email === 'string' && m.email.includes('@') && (!exclude || m.user_id !== exclude))
    .sort((a, b) => Number(b.user_id === prefer) - Number(a.user_id === prefer))
    .slice(0, compact ? 1 : 3);
  const url = typeof location !== 'undefined' ? location.href : '';
  const body = encodeURIComponent(`${subject}\n\n${url}`);
  const copy = async () => {
    setCopied('');
    try { await navigator.clipboard.writeText(url); window.setTimeout(() => setCopied('Link copied.'), 50); }
    catch { window.setTimeout(() => setCopied('Copy was unavailable. Copy the address bar instead.'), 50); }
  };
  const firstName = (n: string) => n.split(/\s+/)[0] || n;
  const links = admins.map(a => <a key={a.user_id} className={primary ? 'gp-linkbtn btn-primary' : undefined} href={`mailto:${a.email}?subject=${encodeURIComponent(subject)}&body=${body}`}>
    {compact && verb ? `Ask ${firstName(a.display_name || a.email)} to ${verb}` : `Ask ${a.display_name || a.email}`}
  </a>);
  const fallback = <>
    <Button variant={compact ? 'ghost' : 'secondary'} onClick={() => void copy()}>Copy link to this page</Button>
    {q.error && <Button variant="ghost" onClick={() => void q.refetch()}>Retry</Button>}
    <span role="status" className={copied ? '' : 'sr-only'}>{copied}</span>
  </>;
  if (compact) {
    return <span className="v96-ask compact">
      {q.isLoading ? <span className="v96-ask-skel" aria-label="Loading workspace admins" role="img" /> : links.length > 0 ? links : fallback}
      <span className="sr-only">{`${lead} ${what}.`}</span>
    </span>;
  }
  const shown = single && !more ? links.slice(0, 1) : links;
  return <div className="v96-ask">
    <span>{lead} {what}.</span>
    {q.isLoading ? <span className="v96-ask-skel" aria-label="Loading workspace admins" role="img" /> : links.length > 0 ? shown : fallback}
    {single && !more && links.length > 1 ? <button type="button" className="v96-ask-more" aria-expanded={false} onClick={() => setMore(true)}>or someone else</button> : null}
  </div>;
}
